import { useState, useEffect, FormEvent } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { authAPI } from '../lib/api'
import { Button, Card, PasswordInput, Logo } from '../components/ui'

type LinkState = 'checking' | 'valid' | 'invalid'

// Mirrors AUTH_PASSWORD_VALIDATORS' MinimumLengthValidator (12, not Django's
// default 8). The server is still the authority — this only avoids a round
// trip for the most common miss.
const MIN_PASSWORD_LENGTH = 12

export default function ResetPassword() {
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>()
  const navigate = useNavigate()

  const [linkState, setLinkState] = useState<LinkState>('checking')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [linkError, setLinkError] = useState('')
  const [done, setDone] = useState(false)
  const [loading, setLoading] = useState(false)

  // Check the link up front so an expired one says so before the user types out
  // a new password rather than after.
  useEffect(() => {
    if (!uidb64 || !token) {
      setLinkError('This password reset link is malformed.')
      setLinkState('invalid')
      return
    }
    authAPI
      .verifyPasswordResetToken(uidb64, token)
      .then(() => setLinkState('valid'))
      .catch((err: any) => {
        setLinkError(err.message || 'This password reset link is invalid or has expired.')
        setLinkState('invalid')
      })
  }, [uidb64, token])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('The two passwords do not match.')
      return
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }

    setLoading(true)
    try {
      await authAPI.confirmPasswordReset(uidb64!, token!, password)
      setDone(true)
      // Brief pause so the confirmation is actually readable before the redirect.
      setTimeout(() => navigate('/'), 2000)
    } catch (err: any) {
      setError(err.message || 'Password reset failed.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center p-4"
      style={{
        backgroundImage: 'url(/ISS_earth.avif)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />

      <Card className="relative z-10 w-full max-w-md gap-6 p-6 shadow-2xl">
        <div className="flex flex-col items-center gap-2 text-center">
          <Logo className="mx-auto h-7 w-[231px] max-w-full" />
          <h1 className="text-base font-normal text-muted-foreground">Choose a new password</h1>
        </div>

        {linkState === 'checking' && (
          <p className="text-center text-sm text-muted-foreground">Checking your link&hellip;</p>
        )}

        {linkState === 'invalid' && (
          <div className="flex flex-col items-center gap-3 text-center">
            <div
              role="alert"
              className="w-full rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {linkError}
            </div>
            <Link
              to="/forgot-password"
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Request a new link
            </Link>
          </div>
        )}

        {linkState === 'valid' && done && (
          <div className="flex flex-col items-center gap-3 text-center">
            <div
              role="status"
              className="w-full rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-sm text-success"
            >
              Your password has been reset. Taking you to the login page&hellip;
            </div>
            <Link
              to="/"
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Continue to login
            </Link>
          </div>
        )}

        {linkState === 'valid' && !done && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <PasswordInput
                id="reset-password"
                label="New password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="h-11"
                helpText={`At least ${MIN_PASSWORD_LENGTH} characters.`}
              />
              <PasswordInput
                id="reset-password-confirm"
                label="Confirm new password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                className="h-11"
              />
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              className="h-11 w-full text-base"
              isLoading={loading}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Set new password'}
            </Button>

            <Link
              to="/"
              className="text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Back to login
            </Link>
          </form>
        )}

        <p className="text-center text-xs text-muted-foreground">
          DoW Acquisitions &amp; Vendor Evaluation Platform
        </p>
      </Card>
    </div>
  )
}
